import { ApolloClient, CombinedGraphQLErrors, CombinedProtocolErrors, HttpLink, InMemoryCache, makeVar, } from "@apollo/client";
import { ApolloLink } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { ErrorLink } from "@apollo/client/link/error";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { getMainDefinition } from "@apollo/client/utilities";
import * as Sentry from "@sentry/react";
import { createClient } from "graphql-ws";
import _ from "lodash";
import { setContext as setSentryContext } from "@/app/sentry";
import { getAuthorizationToken } from "../authorizationToken";
import { API_URI, GRAPHQL_URI_WS_RS } from "./utils";
const httpLink = new HttpLink({ uri: `${API_URI}/graphql` });
export const wsAvailableVar = makeVar(true);
const authLink = setContext((_, { headers }) => {
    const token = getAuthorizationToken();
    return {
        headers: {
            ...headers,
            authorization: token ? `Bearer ${token}` : "",
        },
    };
});
let wsLink = null;
function createWsLink() {
    return new GraphQLWsLink(createClient({
        url: GRAPHQL_URI_WS_RS,
        shouldRetry: () => true,
        lazy: true,
        keepAlive: 10_000,
        on: {
            opened: () => wsAvailableVar(true),
            closed: () => wsAvailableVar(false),
        },
    }));
}
try {
    wsLink = createWsLink();
}
catch {
    wsAvailableVar(false);
}
export const errorLink = new ErrorLink(({ error, operation, result }) => {
    const opName = operation?.operationName || "";
    const opVars = operation?.variables || {};
    if (CombinedGraphQLErrors.is(error)) {
        error.errors
            .filter(({ message }) => message !== "not_found")
            .forEach(({ message, locations, path, extensions }) => {
            if (["login", "refresh"].includes(opName))
                return;
            console.error(`[GraphQL error]: Message: ${message}, Location:`, locations, "Path:", path, "extensions", extensions, "Operation:", opName, operation);
            if (process.env.NODE_ENV === "production") {
                const variables = _.omit(opVars, ["password", "values.password"]);
                const errorContext = {
                    extensions: JSON.stringify(extensions, undefined, 2),
                    locations: JSON.stringify(locations, undefined, 2),
                    path: JSON.stringify(path, undefined, 2),
                    operationName: opName,
                    operation: JSON.stringify(result, undefined, 2),
                    variables: JSON.stringify(variables, undefined, 2),
                };
                const err = new Error(message);
                err.name = "graphQLError";
                if ("captureStackTrace" in Error)
                    Error.captureStackTrace(err);
                Sentry.setTag("type", "graphql");
                Sentry.setContext("graphqlContext", errorContext);
                setSentryContext();
                Sentry.captureException(err);
            }
        });
    }
    else if (CombinedProtocolErrors.is(error)) {
        error.errors.forEach(({ message, extensions }) => {
            console.error(`[Protocol error]: Message: ${message}, Extensions:`, extensions, "Operation:", opName);
        });
        if (process.env.NODE_ENV === "production") {
            const err = new Error("protocol error");
            err.name = "protocolError";
            if ("captureStackTrace" in Error)
                Error.captureStackTrace(err);
            Sentry.setTag("type", "graphql");
            setSentryContext();
            Sentry.captureException(err);
        }
    }
    else if (error) {
        console.error("[error]:", error);
        if (process.env.NODE_ENV === "production") {
            const err = error instanceof Error ? error : new Error(String(error));
            err.name ||= "networkError";
            if ("captureStackTrace" in Error && !err.stack)
                Error.captureStackTrace(err);
            Sentry.setTag("type", "network");
            setSentryContext();
            Sentry.captureException(err);
        }
    }
});
const splitLink = ApolloLink.split(({ query }) => {
    const def = getMainDefinition(query);
    return wsAvailableVar() && def.kind === "OperationDefinition" && def.operation === "subscription";
}, wsLink || httpLink, httpLink);
const link = ApolloLink.from([errorLink, authLink, splitLink]);
const client = new ApolloClient({
    link,
    cache: new InMemoryCache(),
    defaultOptions: {
        watchQuery: {
            fetchPolicy: "cache-and-network",
        },
        query: {
            fetchPolicy: "network-only",
        },
    },
});
export default client;
export const sentryLink = new ApolloLink((operation, forward) => {
    Sentry.addBreadcrumb({
        category: "graphql",
        message: `GraphQL ${operation.operationName}`,
        level: "info",
        data: {
            operationName: operation.operationName,
            variables: JSON.stringify(operation.variables),
        },
    });
    return forward(operation);
});
